import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafb] flex flex-col p-10 lg:p-16">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-[#0033CC] flex items-center justify-center rounded-sm">
          <Mail className="w-4 h-4 text-white" strokeWidth={2} />
        </div>
        <span className="font-heading text-lg font-medium tracking-tight">InboxHQ</span>
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-sm w-full" data-testid="forgot-password-page">
          {!sent ? (
            <>
              <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-3">Reset password</p>
              <h1 className="font-heading text-3xl font-medium tracking-tight mb-2">
                Forgot your password?
              </h1>
              <p className="text-sm text-zinc-600 mb-8 leading-relaxed">
                Enter your work email and we'll send you a link to set a new password.
              </p>

              <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>

                {error && (
                  <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-md">
                    {error}
                  </div>
                )}

                <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#0033CC] hover:bg-[#002299] text-white rounded-md font-medium" data-testid="forgot-submit-button">
                  {submitting ? "Sending…" : "Send reset link"}
                </Button>

                <Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1 justify-center" data-testid="back-to-login">
                  <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Back to sign in
                </Link>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-password-success">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" strokeWidth={1.5} />
              <h1 className="font-heading text-2xl font-medium tracking-tight mb-2">Check your email</h1>
              <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
                If <span className="font-mono text-zinc-900">{email}</span> is an account on Internal Inbox,
                a password reset link is on its way. The link expires in 30 minutes.
              </p>
              <Link to="/login" className="text-sm text-[#0033CC] hover:underline inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-zinc-400">© 2026 Internal Inbox — Shared inbox infrastructure.</div>
    </div>
  );
}
